import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { AgentTestResultPanel } from './AgentTestResultPanel';
import {
  PIPELINE_ROLES,
  TRANSPORT_OPTIONS,
  getId,
  getName,
  matchesRole,
  parseTestError,
  type AgentAssignments,
  type AgentTransport,
  type AgentTransports,
  type KaycooAgent,
  type KaycooAgentsResponse,
  type PipelineRole,
  type TestAgentResponse,
  type TestResult,
} from './agentTypes';

interface AgentConfigCardProps {
  clientId: string | null;
  /** Current value of clients.settings, including agent_assignments + agent_transports. */
  settings:
    | ({
        agent_assignments?: AgentAssignments;
        agent_transports?: AgentTransports;
      } & Record<string, unknown>)
    | null;
  isLoading: boolean;
  onSettingsUpdated: (settings: Record<string, unknown>) => void;
  className?: string;
}

export function AgentConfigCard({
  clientId,
  settings,
  isLoading: parentLoading,
  onSettingsUpdated,
  className,
}: AgentConfigCardProps) {
  const [agents, setAgents] = useState<KaycooAgent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<AgentAssignments>({});
  const [transportDraft, setTransportDraft] = useState<AgentTransports>({});
  const [saving, setSaving] = useState(false);
  const [testingRole, setTestingRole] = useState<PipelineRole | null>(null);
  const [testResults, setTestResults] = useState<Partial<Record<PipelineRole, TestResult>>>({});

  const loadAgents = useCallback(async () => {
    setAgentsLoading(true);
    setError(null);
    try {
      const res = await api.get<KaycooAgentsResponse>('/api/v1/settings/agents');
      setAgents(res.agents);
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.message : 'Failed to load Kaycoo agents.';
      setError(msg);
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  useEffect(() => { void loadAgents(); }, [loadAgents]);

  // Sync drafts from props when parent finishes loading client settings.
  useEffect(() => {
    setDraft(settings?.agent_assignments ?? {});
    setTransportDraft(settings?.agent_transports ?? {});
  }, [settings]);

  const agentsByRole = useMemo(() => {
    const map = new Map<PipelineRole, KaycooAgent[]>();
    for (const role of PIPELINE_ROLES) {
      map.set(role.key, agents.filter((a) => matchesRole(a, role.key)));
    }
    return map;
  }, [agents]);

  const handleAssign = (role: PipelineRole, agentId: string) => {
    setDraft((prev) => {
      if (agentId === '__none__') {
        // Rebuild without the role key (avoid dynamic delete).
        const next: AgentAssignments = {};
        for (const [k, v] of Object.entries(prev)) {
          if (k !== role && v) next[k as PipelineRole] = v;
        }
        return next;
      }
      return { ...prev, [role]: agentId };
    });
  };

  const handleTest = async (role: PipelineRole) => {
    setTestingRole(role);
    try {
      const res = await api.post<TestAgentResponse>('/api/v1/settings/agents/test', { role });
      setTestResults((prev) => ({
        ...prev,
        [role]: { ok: true, output: res.output, tokens_used: res.tokens_used, duration_ms: res.duration_ms },
      }));
      toast.success(`${role} agent responded in ${res.duration_ms}ms`);
    } catch (err: unknown) {
      const parsed = parseTestError(err);
      setTestResults((prev) => ({ ...prev, [role]: parsed }));
      toast.error(`${role} agent: ${parsed.error ?? 'Test failed.'}`);
    } finally {
      setTestingRole(null);
    }
  };

  const copyCfRay = (ray: string) => {
    void navigator.clipboard.writeText(ray);
    toast.success(`Copied cf-ray ${ray}`);
  };

  const handleTransportChange = (role: PipelineRole, mode: AgentTransport) => {
    setTransportDraft((prev) => ({ ...prev, [role]: mode }));
  };

  const handleSave = async () => {
    if (!clientId) return;
    setSaving(true);
    const nextSettings = {
      ...(settings ?? {}),
      agent_assignments: draft,
      agent_transports: transportDraft,
    };
    const { error: dbErr } = await supabase
      .from('clients')
      .update({ settings: nextSettings })
      .eq('id', clientId);
    setSaving(false);
    if (dbErr) {
      toast.error('Failed to save agent assignments.');
      return;
    }
    toast.success('Agent assignments saved.');
    onSettingsUpdated(nextSettings);
  };

  const isLoading = parentLoading || agentsLoading;
  const hasChanges =
    JSON.stringify(draft) !== JSON.stringify(settings?.agent_assignments ?? {}) ||
    JSON.stringify(transportDraft) !== JSON.stringify(settings?.agent_transports ?? {});

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent className="space-y-3">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Agent Assignments</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Pick which Kaycoo agent runs each pipeline step for your tenant. Lists are populated
            live from <code>api.kaycoo.ai</code>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { void loadAgents(); }}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Could not load agents</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {!error && agents.length === 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No agents available</AlertTitle>
            <AlertDescription>
              No Kaycoo agents are bound to this application. Add them in the Kaycoo dashboard
              and click Refresh.
            </AlertDescription>
          </Alert>
        )}

        {PIPELINE_ROLES.map((role) => {
          const matching = agentsByRole.get(role.key) ?? [];
          const fallback = agents.filter((a) => !matching.includes(a));
          const currentId = draft[role.key] ?? '__none__';
          const currentAgent = agents.find((a) => getId(a) === draft[role.key]);
          return (
            <div key={role.key} className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <Label htmlFor={`assign-${role.key}`} className="text-sm font-medium">
                  {role.label}
                </Label>
                {currentAgent && (
                  <span className="flex gap-1.5 shrink-0">
                    {currentAgent.provider && (
                      <Badge variant="secondary">{currentAgent.provider}</Badge>
                    )}
                    {currentAgent.model && (
                      <Badge variant="outline">{currentAgent.model}</Badge>
                    )}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <select
                  id={`assign-${role.key}`}
                  value={currentId}
                  onChange={(e) => { handleAssign(role.key, e.target.value); }}
                  className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="__none__">— Not assigned —</option>
                  {matching.length > 0 && (
                    <optgroup label={`${role.label} variants`}>
                      {matching.map((a) => (
                        <option key={getId(a)} value={getId(a)}>{getName(a)}</option>
                      ))}
                    </optgroup>
                  )}
                  {fallback.length > 0 && (
                    <optgroup label="Other agents">
                      {fallback.map((a) => (
                        <option key={getId(a)} value={getId(a)}>{getName(a)}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <select
                  aria-label={`${role.label} transport`}
                  value={transportDraft[role.key] ?? 'auto'}
                  onChange={(e) => {
                    handleTransportChange(role.key, e.target.value as AgentTransport);
                  }}
                  className="h-9 w-28 rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  title="auto = try /stream then /execute · execute = /execute only (HF) · stream = /stream only"
                >
                  {TRANSPORT_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">{role.hint}</p>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => { void handleTest(role.key); }}
                  disabled={!draft[role.key] || testingRole !== null || hasChanges}
                  title={hasChanges ? 'Save assignments before testing' : draft[role.key] ? 'Send a probe input to this agent' : 'Assign an agent first'}
                >
                  {testingRole === role.key ? 'Testing…' : 'Test'}
                </Button>
              </div>
              {testResults[role.key] && (
                <AgentTestResultPanel
                  result={testResults[role.key] as TestResult}
                  onCopyCfRay={copyCfRay}
                />
              )}
            </div>
          );
        })}

        <div className="flex justify-end pt-2">
          <Button
            onClick={() => { void handleSave(); }}
            disabled={!clientId || saving || !hasChanges}
            size="sm"
          >
            {saving ? 'Saving…' : 'Save Assignments'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
